import { Injectable, signal, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, catchError, tap, map, timeout, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { MockToggleService } from './mock-toggle.service';
import {
  MOCK_DATA_TYPES,
  generateMockId,
} from './mock-data';

export interface MasterTableItem {
  id: number;
  code: string;
  label?: string | null;
  sortOrder: number;
  createdAt: Date;
  updatedAt: Date;
}

interface ApiMasterTableItem {
  id: number;
  code: string;
  label?: string | null;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface PaginatedMasterTableResponse<T> {
  items: T[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

@Injectable({
  providedIn: 'root'
})
export class MasterTablesService {
  private http = inject(HttpClient);
  private mockToggle = inject(MockToggleService);

  private dataTypesData = signal<MasterTableItem[]>([]);

  private normalizeItem(apiItem: ApiMasterTableItem): MasterTableItem {
    return {
      id: apiItem.id,
      code: apiItem.code,
      label: apiItem.label,
      sortOrder: apiItem.sortOrder,
      createdAt: new Date(apiItem.createdAt),
      updatedAt: new Date(apiItem.updatedAt)
    };
  }

  private useMock(): boolean {
    return this.mockToggle.isMockEnabled();
  }

  private initDataTypes(): MasterTableItem[] {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const data = JSON.parse(JSON.stringify(MOCK_DATA_TYPES)).map((i: any) => ({ ...i, createdAt: new Date(i.createdAt), updatedAt: new Date(i.updatedAt) }));
    this.dataTypesData.set(data);
    return data;
  }

  getDataTypes(): Observable<MasterTableItem[]> {
    if (this.useMock()) {
      return of(this.initDataTypes());
    }
    return this.http.get<ApiMasterTableItem[]>(`${environment.apiUrl}/datatypes`).pipe(
      timeout(8000),
      map(items => items.map(item => this.normalizeItem(item))),
      tap(items => this.dataTypesData.set(items)),
      catchError(error => {
        console.error('Error fetching datatypes from API:', error);
        return of([]);
      })
    );
  }

  getDataTypesPaginated(page = 1, pageSize = 20): Observable<PaginatedMasterTableResponse<MasterTableItem>> {
    if (this.useMock()) {
      const all = this.initDataTypes();
      return of(this.paginateMockData(all, page, pageSize));
    }

    return this.http
      .get<PaginatedMasterTableResponse<ApiMasterTableItem>>(`${environment.apiUrl}/datatypes/paginated`, {
        params: { page: String(page), pageSize: String(pageSize) },
      })
      .pipe(
        map((response) => ({
          ...response,
          items: response.items.map((item) => this.normalizeItem(item)),
        })),
        catchError((error) => {
          console.error('Error fetching paginated datatypes from API:', error);
          return of({ items: [], page, pageSize, total: 0, totalPages: 0 });
        })
      );
  }

  addDataType(item: Omit<MasterTableItem, 'id' | 'createdAt' | 'updatedAt'>): Observable<MasterTableItem> {
    if (this.useMock()) {
      return this.mockAddItem(item, 'dtype-', this.dataTypesData);
    }
    return this.http.post<ApiMasterTableItem>(`${environment.apiUrl}/datatypes`, item).pipe(
      map(apiItem => this.normalizeItem(apiItem)),
      tap(newItem => {
        this.dataTypesData.update(items => [...items, newItem]);
      }),
      catchError(error => {
        console.error('Error adding datatype to API:', error);
        return throwError(() => error);
      })
    );
  }

  updateDataType(id: number, item: Partial<MasterTableItem>): Observable<MasterTableItem> {
    if (this.useMock()) {
      return this.mockUpdateItem(id, item, this.dataTypesData);
    }
    return this.http.put<ApiMasterTableItem>(`${environment.apiUrl}/datatypes/${String(id)}`, item).pipe(
      map(apiItem => this.normalizeItem(apiItem)),
      tap(updated => {
        this.dataTypesData.update(arr => arr.map(i => i.id === id ? updated : i));
      }),
      catchError(error => {
        console.error('Error updating datatype in API:', error);
        return throwError(() => error);
      })
    );
  }

  deleteDataType(id: number): Observable<boolean> {
    if (this.useMock()) {
      this.dataTypesData.update(items => items.filter(i => i.id !== id));
      return of(true);
    }
    return this.http.delete(`${environment.apiUrl}/datatypes/${String(id)}`).pipe(
      map(() => {
        this.dataTypesData.update(items => items.filter(i => i.id !== id));
        return true;
      }),
      catchError(error => {
        console.error('Error deleting datatype from API:', error);
        return throwError(() => error);
      })
    );
  }

  private mockAddItem(
    item: Omit<MasterTableItem, 'id' | 'createdAt' | 'updatedAt'>,
    prefix: string,
    dataSignal: ReturnType<typeof signal<MasterTableItem[]>>
  ): Observable<MasterTableItem> {
    const newItem: MasterTableItem = {
      ...item,
      id: generateMockId(prefix),
      createdAt: new Date(),
      updatedAt: new Date(),
    };
    dataSignal.update(items => [...items, newItem]);
    return of(newItem);
  }

  private mockUpdateItem(
    id: number,
    item: Partial<MasterTableItem>,
    dataSignal: ReturnType<typeof signal<MasterTableItem[]>>
  ): Observable<MasterTableItem> {
    let updated: MasterTableItem = {} as MasterTableItem;
    dataSignal.update(arr => arr.map(i => {
      if (i.id === id) {
        updated = { ...i, ...item, updatedAt: new Date() };
        return updated;
      }
      return i;
    }));
    return of(updated);
  }

  private paginateMockData(
    all: MasterTableItem[],
    page: number,
    pageSize: number,
  ): PaginatedMasterTableResponse<MasterTableItem> {
    const total = all.length;
    const totalPages = total > 0 ? Math.ceil(total / pageSize) : 0;
    const boundedPage = totalPages > 0 ? Math.min(Math.max(page, 1), totalPages) : 1;
    const start = (boundedPage - 1) * pageSize;
    return {
      items: all.slice(start, start + pageSize),
      page: boundedPage,
      pageSize,
      total,
      totalPages,
    };
  }
}
